import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../api/bootstrap';
import { usePrecargarAvatar } from '../../avatar/useAvatar';
import { Button } from '../../components/Button';
import { PiePagina } from '../../components/PiePagina';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Spinner } from '../../components/Spinner';
import { esSinSimulacion, formatError } from '../../lib/formatError';
import { useAuthStore } from '../../stores/authStore';
import { useMiSimulacion } from './useMiSimulacion';

export function MiSimulacionPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch, isRefetching } = useMiSimulacion();
  const usuarioNombre = useAuthStore((s) => s.usuarioNombre);
  const tenantNombre = useAuthStore((s) => s.tenantNombre);
  // El `.glb` del escenario empieza a bajar acá, antes de entrar a la
  // entrevista (D-6). La pantalla de sesión no espera a que termine.
  usePrecargarAvatar(data?.escenario.avatar);

  const handleLogout = async () => {
    await getApi().authClient.logout();
    useAuthStore.getState().reset();
    navigate('/login', { replace: true });
  };

  return (
    <main className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">{usuarioNombre ?? '—'}</p>
            <p className="truncate text-xs text-slate-500">{tenantNombre ?? '—'}</p>
          </div>
          <Button variant="secondary" onClick={handleLogout}>
            <LogOut size={16} />
            <span className="hidden sm:inline">Salir</span>
          </Button>
        </div>
      </header>
      <section className="mx-auto w-full max-w-3xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Spinner size={28} />
          </div>
        )}
        {error && esSinSimulacion(error) && (
          <div className="space-y-4 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
            <p className="text-sm text-slate-700">{formatError(error, 'mi_simulacion')}</p>
            <Button variant="secondary" onClick={() => void refetch()} disabled={isRefetching}>
              {isRefetching ? <Spinner /> : 'Actualizar'}
            </Button>
          </div>
        )}
        {error && !esSinSimulacion(error) && (
          <ErrorMessage>{formatError(error, 'mi_simulacion')}</ErrorMessage>
        )}
        {data && (
          <article className="space-y-4 rounded-2xl bg-white p-5 shadow-sm sm:p-6">
            <header>
              <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                {data.escenario.nombre}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {data.escenario.duracionMaxMin} min · modalidad{' '}
                <span className="font-medium">{data.simulacion.modalidad}</span>
              </p>
            </header>
            {data.escenario.introduccionTexto && (
              <p className="text-sm leading-relaxed text-slate-700">
                {data.escenario.introduccionTexto}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div>
                <dt className="font-medium text-slate-500">Estado</dt>
                <dd className="text-slate-900">{data.asignacion.estado}</dd>
              </div>
              <div>
                <dt className="font-medium text-slate-500">Disponible hasta</dt>
                <dd className="text-slate-900">{data.asignacion.disponibleHasta}</dd>
              </div>
              <div>
                <dt className="font-medium text-slate-500">Avatar</dt>
                <dd className="text-slate-900">{data.escenario.avatar.nombre}</dd>
              </div>
              <div>
                <dt className="font-medium text-slate-500">Reconexión</dt>
                <dd className="text-slate-900">{data.reconexion ? 'Sí' : 'No'}</dd>
              </div>
            </dl>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => navigate(`/simulacion/${data.simulacion.id}`)}>
                {data.reconexion ? 'Retomar entrevista' : 'Ir a la entrevista'}
              </Button>
              {/* Refetch manual y explícito: el GET crea y abandona simulaciones (D-2). */}
              <Button variant="secondary" onClick={() => void refetch()} disabled={isRefetching}>
                {isRefetching ? <Spinner /> : 'Actualizar'}
              </Button>
            </div>
          </article>
        )}
      </section>
      <PiePagina />
    </main>
  );
}
